/**
 * template/sections/education/EducationExamHomeSection.jsx
 *
 * AVPS Academy "Exam Portal" home template — READER (published page) side.
 * Converted 1:1 from the standalone HTML/CSS design so it renders IDENTICALLY
 * to the Console builder preview version
 * (admin-console-ui/src/modules/template-builder/sections/EducationExamHomeSection.jsx).
 *
 * This is an ADDITIONAL selectable Education section type (`educationExamHome`) — it
 * does not modify or replace any existing Education section or rendering path. It is
 * wired into the section registry exactly like every other Education section (see
 * TemplateSectionRegistry.jsx `educationSectionComponents` + the component map in
 * TemplateSectionContentRenderer.jsx).
 *
 * STYLE ISOLATION: every selector below is scoped under the single root class
 * `.aae-home`, so this section cannot leak styles into the rest of the reader and
 * needs no rules in EducationExamTemplate.css.
 *
 * CHROME: this section renders the design's own lime-pill nav and dark footer, so the
 * Exam template sets `attributes.header.visible = false` and
 * `attributes.footer.visible = false` (see educationExamTemplate in TemplateStorage.jsx).
 * TemplateRenderer gates the Education chrome on those flags, so the page renders
 * exactly one nav and one footer.
 */

import React from 'react';
import { useSelector } from 'react-redux';

import {
  getCompanyLogoUrl,
  getCompanyPolicyLinks,
  getTemplateCompanyContext,
  selectTemplateCompanyState
} from '../../TemplateCompanyUtils.js';

/** Default hero photograph, shared verbatim with the Console twin so both render alike. */
export const DEFAULT_STUDENT_IMAGE = 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/landingPage.jpg';

/**
 * Design tokens, shared chrome (nav + footer) and the home-page blocks, all scoped under
 * `.aae-home`. The inner Exam pages (EducationExamPagesSection) reuse this same block for
 * the tokens and chrome so the header/footer cannot drift between pages.
 */
export const EXAM_HOME_CSS = `
.aae-home{--aae-teal-950:#173f3d;--aae-teal-900:#1f5a54;--aae-teal-700:#2b8b77;--aae-lime:#d8ff91;--aae-lime-soft:#e7ffb8;--aae-orange:#ffb536;--aae-ink:#171918;--aae-paper:#f4f0eb;--aae-purple:#7438ee;--aae-blue:#2c9eea;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:var(--aae-ink);background:var(--aae-paper);overflow:clip}
.aae-home *{box-sizing:border-box}
.aae-home a{color:inherit;text-decoration:none}
.aae-home h1,.aae-home h2,.aae-home h3,.aae-home h4,.aae-home p{margin:0}
.aae-home .aae-wrap{width:min(1240px,calc(100% - 80px));margin:0 auto}
.aae-home .aae-kicker{color:var(--aae-teal-700);font-weight:900;text-transform:uppercase;font-size:13px;letter-spacing:1.8px}
.aae-home .aae-title{margin:9px 0 12px;font-size:48px;letter-spacing:-2px;line-height:1.04}
.aae-home .aae-lead{max-width:670px;color:#626966;font-size:17px;line-height:1.7}
.aae-home .aae-btn{display:inline-flex;align-items:center;justify-content:center;min-height:58px;padding:0 27px;border-radius:999px;border:1.5px solid rgba(255,255,255,.72);font-weight:900;font-size:15px;transition:.22s ease;cursor:pointer}
.aae-home .aae-btn-primary{color:#151515;border-color:var(--aae-orange);background:var(--aae-orange);box-shadow:0 14px 34px rgba(124,72,0,.24)}
.aae-home .aae-btn-secondary{color:#fff;background:rgba(255,255,255,.035)}
.aae-home .aae-btn:hover{transform:translateY(-3px);box-shadow:0 16px 34px rgba(16,55,50,.2)}

.aae-home .aae-nav{width:min(1280px,calc(100% - 80px));margin:0 auto;padding:24px 0 10px;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:28px;position:relative;z-index:5}
.aae-home .aae-hero-nav-slot{height:78px}
.aae-home .aae-hero-nav-slot .aae-nav{position:fixed;inset:0 0 auto;width:100%;max-width:none;margin:0;padding:16px max(40px,calc((100vw - 1280px) / 2)) 10px;background:rgba(33,121,103,.97);box-shadow:0 10px 30px rgba(16,55,50,.2);backdrop-filter:blur(10px)}
.aae-home .aae-brand{font-size:27px;font-weight:900;letter-spacing:-1.2px;color:#fff}
.aae-home .aae-brand span{color:var(--aae-lime)}
.aae-home .aae-brand img{display:block;height:44px;width:auto;max-width:220px;object-fit:contain}
.aae-home .aae-nav-pill{display:flex;align-items:center;gap:4px;background:var(--aae-lime-soft);color:#111;border:1px solid rgba(255,255,255,.55);border-radius:999px;padding:6px;box-shadow:0 12px 30px rgba(22,71,64,.13)}
.aae-home .aae-nav-pill a{padding:11px 18px;border-radius:999px;font-weight:700;font-size:14px;color:#101312;transition:.2s ease}
.aae-home .aae-nav-pill a:hover,.aae-home .aae-nav-pill a[aria-current="page"]{background:rgba(255,255,255,.64);transform:translateY(-1px)}
.aae-home .aae-nav-actions{display:flex;justify-content:flex-end;align-items:center;gap:15px}
.aae-home .aae-login{font-weight:700;font-size:14px;color:#fff}
.aae-home .aae-register{color:#111;background:#fff;border:0;border-radius:999px;padding:13px 23px;font-weight:800;box-shadow:0 12px 30px rgba(18,62,57,.15)}
.aae-home .aae-account{display:inline-flex;align-items:center;gap:11px;padding:6px 18px 6px 6px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.28);color:#fff;font-weight:800;font-size:14px;transition:.2s ease}
.aae-home .aae-account:hover{background:rgba(255,255,255,.2)}
.aae-home .aae-account-avatar{width:36px;height:36px;flex:0 0 36px;display:grid;place-items:center;border-radius:50%;overflow:hidden;background:var(--aae-lime);color:#15302c;font-weight:900;font-size:13px}
.aae-home .aae-account-avatar img{width:100%;height:100%;object-fit:cover;display:block}
.aae-home .aae-account-name{max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
/* Standalone chrome bar. On designed pages the nav sits transparent on the teal hero; on the
   FUNCTIONAL education routes (attendance, myCourses, profile, …) there is no hero behind it,
   so the shared chrome renders the nav on its own gradient band. */
.aae-home .aae-chrome-bar{background:linear-gradient(126deg,#217967 0%,#258d79 46%,#2a9a85 100%);box-shadow:0 10px 30px rgba(16,55,50,.16)}
.aae-home .aae-chrome-bar .aae-nav{padding:16px 0}
.aae-home .aae-chrome-footer{background:#112f2d}
.aae-home .aae-chrome-footer .aae-site-footer{padding:56px 0 24px}

.aae-home .aae-hero{position:relative;min-height:715px;color:#fff;background:radial-gradient(circle at 70% 15%,rgba(255,255,255,.10),transparent 28%),linear-gradient(126deg,#217967 0%,#258d79 46%,#2a9a85 100%);isolation:isolate}
.aae-home .aae-wave{position:absolute;left:-4%;right:-4%;height:34px;border-radius:50%;border-top:13px solid rgba(13,102,89,.12);transform:rotate(-4deg);pointer-events:none}
.aae-home .aae-wave.w1{top:208px}
.aae-home .aae-wave.w2{top:290px;transform:rotate(-6deg);opacity:.75}
.aae-home .aae-wave.w3{top:396px;transform:rotate(-7deg);opacity:.55}
.aae-home .aae-wave.w4{top:500px;transform:rotate(-5deg);opacity:.42}
.aae-home .aae-hero-grid{width:min(1280px,calc(100% - 80px));margin:0 auto;display:grid;grid-template-columns:minmax(0,1.02fr) minmax(470px,.98fr);align-items:center;gap:58px;padding:74px 0 80px;position:relative;z-index:3}
.aae-home .aae-eyebrow{display:inline-flex;align-items:center;gap:10px;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.18);border-radius:999px;padding:10px 15px;font-size:13px;font-weight:800;letter-spacing:.25px;margin-bottom:22px}
.aae-home .aae-eyebrow::before{content:"";width:9px;height:9px;border-radius:50%;background:var(--aae-orange);box-shadow:0 0 0 5px rgba(255,181,54,.18)}
.aae-home .aae-hero h1{max-width:660px;font-family:"Arial Narrow",Impact,Haettenschweiler,sans-serif;font-size:clamp(64px,7vw,104px);line-height:.88;letter-spacing:-4px;text-transform:uppercase;text-shadow:0 7px 0 rgba(20,80,72,.07)}
.aae-home .aae-hero h1 .aae-accent{color:var(--aae-lime);display:block}
.aae-home .aae-hero-copy{max-width:580px;margin:25px 0 30px;color:rgba(255,255,255,.82);font-size:17px;line-height:1.65}
.aae-home .aae-cta-row{display:flex;gap:15px;flex-wrap:wrap;align-items:center}
.aae-home .aae-micro-stats{display:flex;gap:26px;margin-top:32px;padding-top:24px;border-top:1px solid rgba(255,255,255,.22);max-width:575px}
.aae-home .aae-micro-stat strong{display:block;font-size:24px}
.aae-home .aae-micro-stat span{color:rgba(255,255,255,.67);font-size:13px}

.aae-home .aae-visual{position:relative;min-height:505px}
.aae-home .aae-orbit{position:absolute;width:425px;height:330px;top:18px;right:4px;border:4px solid var(--aae-lime);border-radius:48% 52% 46% 54%/62% 44% 56% 38%;transform:rotate(-11deg);opacity:.95}
.aae-home .aae-orbit::after{content:"";position:absolute;width:22px;height:22px;right:28px;top:-12px;background:var(--aae-orange);border:5px solid rgba(255,255,255,.85);border-radius:50%}
.aae-home .aae-student-photo{position:absolute;top:46px;right:36px;width:410px;height:310px;object-fit:cover;object-position:center;border-radius:48% 52% 44% 56%/63% 42% 58% 37%;box-shadow:0 35px 60px rgba(10,54,49,.32)}
.aae-home .aae-progress-card{position:absolute;left:-6px;bottom:8px;width:355px;color:var(--aae-ink);background:rgba(255,255,255,.97);border:1px solid rgba(255,255,255,.9);border-radius:26px;padding:22px;box-shadow:0 26px 60px rgba(13,59,54,.28);animation:aae-float 5s ease-in-out infinite}
@keyframes aae-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
.aae-home .aae-progress-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px}
.aae-home .aae-progress-head h3{margin:0 0 3px;font-size:20px}
.aae-home .aae-progress-head small{color:#8a918e}
.aae-home .aae-progress-num{font-size:42px;line-height:1;font-weight:900;letter-spacing:-2px}
.aae-home .aae-bar{margin:17px 0 19px;height:7px;overflow:hidden;background:#edf0ed;border-radius:999px}
.aae-home .aae-bar span{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--aae-purple),var(--aae-blue),var(--aae-orange))}
.aae-home .aae-lesson-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:9px}
.aae-home .aae-lesson{padding:13px 10px;background:#f7f8f6;border-radius:16px;text-align:center;border:1px solid #f0f1ef}
.aae-home .aae-lesson i{display:grid;place-items:center;width:30px;height:30px;margin:0 auto 8px;border-radius:50%;font-style:normal;color:#fff;font-weight:900;font-size:13px}
.aae-home .aae-lesson:nth-child(1) i{background:var(--aae-purple)}
.aae-home .aae-lesson:nth-child(2) i{background:var(--aae-blue)}
.aae-home .aae-lesson:nth-child(3) i{background:var(--aae-orange)}
.aae-home .aae-lesson strong{display:block;font-size:13px}
.aae-home .aae-lesson span{color:#7c8481;font-size:11px}
.aae-home .aae-floating-badge{position:absolute;right:7px;bottom:46px;padding:14px 16px;border-radius:18px;color:#131313;background:var(--aae-lime);box-shadow:0 18px 46px rgba(21,54,51,.14);font-weight:900;font-size:13px;transform:rotate(4deg)}

.aae-home .aae-growth{background:var(--aae-lime);padding:36px 0 34px}
.aae-home .aae-growth-title{margin:0 0 28px;font-size:31px;font-weight:800;letter-spacing:-1.25px}
/* auto-fit rather than a fixed 5 columns: the band is fed by live categories, so a company
   with 2 categories fills the row instead of leaving 3 empty columns. */
.aae-home .aae-track-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px}
.aae-home .aae-track{min-height:76px;display:flex;align-items:center;justify-content:center;gap:10px;text-align:center;background:rgba(255,255,255,.24);border:1px solid rgba(31,90,84,.14);border-radius:18px;font-weight:800;color:#40524e;padding:14px}
.aae-home .aae-track img{width:42px;height:42px;flex:0 0 42px;border-radius:12px;object-fit:cover}

.aae-home .aae-paths{padding:88px 0 96px;background:var(--aae-paper)}
.aae-home .aae-cards{margin-top:36px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}
.aae-home .aae-card{min-height:230px;padding:28px;border-radius:28px;background:#fff;box-shadow:0 18px 45px rgba(45,56,52,.08);border:1px solid rgba(44,67,61,.08);overflow:hidden;position:relative;display:flex;flex-direction:column}
.aae-home .aae-card h3{margin:0 0 8px;font-size:24px;letter-spacing:-.7px}
.aae-home .aae-card p{color:#68706d;line-height:1.6;max-width:360px}
.aae-home .aae-card:nth-child(1){background:linear-gradient(135deg,#d8d1ff 0%,#b7eaff 100%)}
.aae-home .aae-card:nth-child(2){background:linear-gradient(135deg,#c7f18e 0%,#a6e6d3 100%)}
.aae-home .aae-card:nth-child(3){background:linear-gradient(135deg,#ffe0a6 0%,#ffd0bf 100%)}
.aae-home .aae-card .aae-num{position:absolute;right:24px;bottom:8px;font-size:96px;line-height:1;font-weight:900;color:rgba(255,255,255,.55);letter-spacing:-7px}
.aae-home .aae-tag{display:inline-block;align-self:flex-start;margin-top:auto;padding:8px 12px;border:1px solid rgba(0,0,0,.12);background:rgba(255,255,255,.42);border-radius:999px;font-size:12px;font-weight:800}

.aae-home .aae-courses{padding:92px 0 100px;background:#fff}
.aae-home .aae-head{display:flex;align-items:flex-end;justify-content:space-between;gap:28px;margin-bottom:36px}
.aae-home .aae-head .aae-title{margin-bottom:0;max-width:690px}
.aae-home .aae-textlink{display:inline-flex;align-items:center;gap:10px;white-space:nowrap;color:var(--aae-teal-900);font-weight:900;padding-bottom:7px;border-bottom:2px solid var(--aae-teal-900)}
.aae-home .aae-course-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.aae-home .aae-course-card{min-height:430px;border-radius:30px;overflow:hidden;background:var(--aae-paper);border:1px solid rgba(31,90,84,.09);box-shadow:0 20px 45px rgba(30,59,54,.08);transition:transform .25s ease,box-shadow .25s ease}
.aae-home .aae-course-card:hover{transform:translateY(-8px);box-shadow:0 28px 60px rgba(30,59,54,.14)}
.aae-home .aae-course-art{position:relative;height:220px;margin:12px;border-radius:22px;overflow:hidden;display:flex;align-items:center;justify-content:center;isolation:isolate}
.aae-home .aae-course-art .aae-product-image{position:absolute;inset:0;z-index:1;width:100%;height:100%;display:block;object-fit:cover;background:#fff}
.aae-home .aae-course-art .aae-course-symbol{position:relative;z-index:2}
.aae-home .aae-course-art.has-product-image{background:#fff}
.aae-home .aae-course-art.has-product-image::before,.aae-home .aae-course-art.has-product-image::after{display:none}
.aae-home .aae-course-art::before,.aae-home .aae-course-art::after{content:"";position:absolute;border-radius:50%;z-index:-1}
.aae-home .aae-course-art::before{width:190px;height:190px;right:-35px;top:-48px;background:rgba(255,255,255,.35)}
.aae-home .aae-course-art::after{width:115px;height:115px;left:-26px;bottom:-30px;border:22px solid rgba(255,255,255,.28)}
.aae-home .aae-course-art.a1{background:linear-gradient(135deg,#bfd8ff,#d8c8ff)}
.aae-home .aae-course-art.a2{background:linear-gradient(135deg,#c3f3bf,#9ce4d6)}
.aae-home .aae-course-art.a3{background:linear-gradient(135deg,#ffd49d,#ffc3b8)}
.aae-home .aae-course-symbol{width:118px;height:118px;display:grid;place-items:center;border-radius:36px;background:rgba(255,255,255,.73);border:1px solid rgba(255,255,255,.8);box-shadow:0 18px 42px rgba(55,64,88,.15);color:var(--aae-ink);font-size:44px;font-weight:900;transform:rotate(-6deg)}
.aae-home .aae-course-card:nth-child(2) .aae-course-symbol{transform:rotate(5deg)}
.aae-home .aae-course-card:nth-child(3) .aae-course-symbol{transform:rotate(-3deg)}
.aae-home .aae-course-body{padding:12px 25px 27px}
.aae-home .aae-course-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.aae-home .aae-pill{display:inline-flex;align-items:center;min-height:28px;padding:0 11px;border-radius:999px;background:#fff;color:var(--aae-teal-900);border:1px solid rgba(31,90,84,.11);font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.6px}
.aae-home .aae-course-card h3{margin:15px 0 9px;font-size:25px;letter-spacing:-.8px}
.aae-home .aae-course-card p{color:#68706d;line-height:1.55}
.aae-home .aae-course-foot{display:flex;align-items:center;justify-content:space-between;gap:15px;margin-top:22px;padding-top:17px;border-top:1px solid rgba(29,61,55,.10);font-size:13px;color:#5e6864;font-weight:700}
.aae-home .aae-course-foot strong{color:var(--aae-ink)}

.aae-home .aae-experience{padding:100px 0;background:var(--aae-teal-950);color:#fff;position:relative;overflow:hidden}
.aae-home .aae-experience::before{content:"";position:absolute;width:660px;height:660px;right:-270px;top:-250px;border:80px solid rgba(216,255,145,.08);border-radius:50%}
.aae-home .aae-experience-grid{display:grid;grid-template-columns:.92fr 1.08fr;gap:75px;align-items:center;position:relative;z-index:1}
.aae-home .aae-experience .aae-kicker{color:var(--aae-lime)}
.aae-home .aae-experience .aae-title{color:#fff;max-width:620px}
.aae-home .aae-experience .aae-lead{color:rgba(255,255,255,.68)}
.aae-home .aae-feature-list{margin-top:30px;display:grid;gap:15px}
.aae-home .aae-feature-item{display:grid;grid-template-columns:46px 1fr;gap:14px;align-items:start;padding:15px 0;border-top:1px solid rgba(255,255,255,.12)}
.aae-home .aae-feature-item i{width:42px;height:42px;display:grid;place-items:center;border-radius:14px;color:#142b28;background:var(--aae-lime);font-style:normal;font-weight:900}
.aae-home .aae-feature-item strong{display:block;margin-bottom:4px;font-size:17px}
.aae-home .aae-feature-item span{color:rgba(255,255,255,.62);font-size:14px;line-height:1.55}
.aae-home .aae-mock{position:relative;min-height:515px;padding:22px;border-radius:34px;background:linear-gradient(145deg,rgba(255,255,255,.13),rgba(255,255,255,.05));border:1px solid rgba(255,255,255,.17);box-shadow:0 36px 80px rgba(0,0,0,.28)}
.aae-home .aae-mock-top{display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:20px}
.aae-home .aae-mock-dots{display:flex;gap:7px}
.aae-home .aae-mock-dots span{width:9px;height:9px;border-radius:50%;background:rgba(255,255,255,.42)}
.aae-home .aae-mock-user{width:38px;height:38px;border-radius:50%;display:grid;place-items:center;background:var(--aae-lime);color:#15302c;font-weight:900}
.aae-home .aae-mock-panel{background:#f7f5f1;color:var(--aae-ink);border-radius:25px;padding:24px}
.aae-home .aae-mock-panel h3{font-size:21px}
.aae-home .aae-mock-panel p{color:#7b827f;font-size:13px}
.aae-home .aae-mock-progress{display:grid;grid-template-columns:150px 1fr;gap:24px;align-items:center;margin-top:22px}
.aae-home .aae-ring{--aae-ring-progress:70%;width:142px;height:142px;border-radius:50%;display:grid;place-items:center;background:conic-gradient(var(--aae-purple) 0 var(--aae-ring-progress),#e8e8e8 var(--aae-ring-progress) 100%);position:relative}
.aae-home .aae-ring::before{content:"";width:103px;height:103px;position:absolute;border-radius:50%;background:#f7f5f1}
.aae-home .aae-ring strong{position:relative;font-size:28px;letter-spacing:-1px}
.aae-home .aae-mock-bars{display:grid;gap:16px}
.aae-home .aae-mock-row{display:grid;grid-template-columns:1fr auto;gap:12px;font-size:13px;font-weight:800}
.aae-home .aae-mock-row span:last-of-type{color:#6b726f}
.aae-home .aae-tiny-bar{grid-column:1/-1;height:8px;border-radius:999px;background:#e9e9e5;overflow:hidden}
.aae-home .aae-tiny-bar i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--aae-teal-700),var(--aae-lime))}
.aae-home .aae-mock-small-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:17px}
.aae-home .aae-mock-small{min-height:105px;padding:18px;border-radius:20px;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.13)}
.aae-home .aae-mock-small strong{display:block;font-size:26px;color:var(--aae-lime)}
.aae-home .aae-mock-small span{color:rgba(255,255,255,.67);font-size:13px}

.aae-home .aae-final-cta{padding:72px 0 90px;background:var(--aae-paper)}
.aae-home .aae-cta-panel{position:relative;overflow:hidden;padding:62px 65px;border-radius:36px;color:#fff;background:linear-gradient(120deg,#205d55,#278a77);box-shadow:0 30px 70px rgba(25,73,66,.22);display:grid;grid-template-columns:1fr auto;gap:36px;align-items:center}
.aae-home .aae-cta-panel::before,.aae-home .aae-cta-panel::after{content:"";position:absolute;border-radius:50%;border:45px solid rgba(216,255,145,.13)}
.aae-home .aae-cta-panel::before{width:320px;height:320px;right:-110px;top:-175px}
.aae-home .aae-cta-panel::after{width:180px;height:180px;left:39%;bottom:-155px}
.aae-home .aae-cta-panel>*{position:relative;z-index:1}
.aae-home .aae-cta-panel h2{margin:0 0 12px;max-width:760px;font-size:45px;line-height:1.06;letter-spacing:-2px}
.aae-home .aae-cta-panel p{color:rgba(255,255,255,.72);max-width:660px;line-height:1.65}
.aae-home .aae-cta-panel .aae-btn{border:0;min-width:185px}

.aae-home .aae-site-footer{background:#112f2d;color:#fff;padding:70px 0 26px}
.aae-home .aae-site-footer .aae-brand{color:#fff!important}
.aae-home .aae-footer-grid{display:grid;grid-template-columns:1.5fr repeat(3,1fr);gap:52px}
.aae-home .aae-footer-brand{max-width:380px}
.aae-home .aae-footer-brand .aae-brand{display:inline-block;margin-bottom:17px}
.aae-home .aae-site-footer .aae-footer-brand p{color:rgba(255,255,255,.78)!important;line-height:1.7}
.aae-home .aae-site-footer .aae-footer-col h4{margin:5px 0 18px;color:var(--aae-lime)!important;font-size:13px;text-transform:uppercase;letter-spacing:1.4px}
.aae-home .aae-site-footer .aae-footer-col a{display:block;width:max-content;max-width:100%;margin:0 0 12px;color:rgba(255,255,255,.70)!important;font-size:14px}
.aae-home .aae-site-footer .aae-footer-col a:hover{color:#fff!important}
.aae-home .aae-footer-col a:focus-visible,.aae-home .aae-footer-brand>a:focus-visible,.aae-home .aae-footer-social a:focus-visible{outline:3px solid var(--aae-lime);outline-offset:4px;color:#fff}
.aae-home .aae-site-footer .aae-footer-bottom{display:flex;justify-content:space-between;gap:24px;align-items:center;margin-top:52px;padding-top:22px;border-top:1px solid rgba(255,255,255,.12);color:rgba(255,255,255,.76)!important;font-size:12px}
.aae-home .aae-footer-social{display:flex;gap:9px}
.aae-home .aae-site-footer .aae-footer-social a{width:34px;height:34px;display:grid;place-items:center;border:1px solid rgba(255,255,255,.15);border-radius:50%;color:#fff!important;font-weight:800}

@media(max-width:1080px){
.aae-home .aae-nav{grid-template-columns:1fr auto;width:min(100% - 40px,930px)}
.aae-home .aae-nav-pill{display:none}
.aae-home .aae-footer-grid{grid-template-columns:1.3fr 1fr 1fr}
.aae-home .aae-footer-col:last-child{grid-column:2/4}
.aae-home .aae-course-grid{grid-template-columns:1fr 1fr}
.aae-home .aae-course-card:last-child{grid-column:1/-1}
.aae-home .aae-experience-grid{grid-template-columns:1fr;gap:46px}
.aae-home .aae-mock{max-width:760px;width:100%;margin:0 auto}
.aae-home .aae-hero-grid{width:min(100% - 40px,930px);grid-template-columns:1fr;padding-top:45px}
.aae-home .aae-visual{min-height:545px;max-width:610px;width:100%;margin:0 auto}
.aae-home .aae-wrap{width:min(100% - 40px,930px)}
.aae-home .aae-track-row{grid-template-columns:repeat(2,1fr)}
.aae-home .aae-cards{grid-template-columns:1fr}
}
@media(max-width:640px){
.aae-home .aae-nav{padding-top:18px}
.aae-home .aae-register{padding:11px 16px}
.aae-home .aae-login{display:none}
.aae-home .aae-footer-grid{grid-template-columns:1fr 1fr;gap:38px 24px}
.aae-home .aae-footer-brand{grid-column:1/-1}
.aae-home .aae-footer-col:last-child{grid-column:auto}
.aae-home .aae-footer-bottom{align-items:flex-start;flex-direction:column}
.aae-home .aae-head{align-items:flex-start;flex-direction:column}
.aae-home .aae-course-grid{grid-template-columns:1fr}
.aae-home .aae-course-card:last-child{grid-column:auto}
.aae-home .aae-experience,.aae-home .aae-courses{padding:74px 0}
.aae-home .aae-mock{min-height:auto;padding:15px}
.aae-home .aae-mock-progress{grid-template-columns:1fr;justify-items:center}
.aae-home .aae-mock-small-grid{grid-template-columns:1fr}
.aae-home .aae-cta-panel{grid-template-columns:1fr;padding:43px 28px}
.aae-home .aae-cta-panel h2{font-size:36px}
.aae-home .aae-hero{min-height:auto}
.aae-home .aae-hero-grid{gap:20px;padding:48px 0 60px}
.aae-home .aae-hero h1{font-size:61px;letter-spacing:-2.5px}
.aae-home .aae-hero-copy{font-size:15px}
.aae-home .aae-btn{min-height:53px;padding:0 21px}
.aae-home .aae-micro-stats{gap:18px;flex-wrap:wrap}
.aae-home .aae-visual{min-height:480px;transform:scale(.88);transform-origin:top center;margin-bottom:-50px}
.aae-home .aae-orbit{right:50%;transform:translateX(50%) rotate(-11deg);width:390px}
.aae-home .aae-student-photo{right:50%;transform:translateX(50%);width:380px}
.aae-home .aae-progress-card{left:50%;transform:translateX(-50%);animation:none}
.aae-home .aae-floating-badge{right:22px}
.aae-home .aae-growth-title{font-size:25px}
.aae-home .aae-track-row{grid-template-columns:1fr}
.aae-home .aae-title{font-size:39px}
}
@media(max-width:768px){
.aae-home .aae-cards{grid-template-columns:1fr}
}
@media(prefers-reduced-motion:reduce){
.aae-home .aae-progress-card{animation:none}
.aae-home .aae-course-card{transition:none}
}
`;

/**
 * Content defaults, mirroring the approved design verbatim. Every value is
 * overridable from `section.content` so the section stays editable in the builder.
 */
export const DEFAULTS = {
  brandName: 'Exam portal',
  brandAccent: 'Academy',
  nav: [
    { label: 'Exams', url: '/course' },
    { label: 'About', url: '/about' }
  ],
  signInLabel: 'Login',
  signInUrl: '/sign-in',
  registerLabel: 'Register',
  registerUrl: '/sign-up',
  accountLabel: 'My account',

  eyebrow: 'Secure online assessments, from schedule to certificate',
  titlePrefix: 'Exam',
  titleSuffix: 'Anywhere',
  titleAccent: 'Certify Faster',
  subtitle: 'Publish exam schedules, invite candidates, proctor live attempts and release verified results with section-wise analytics and shareable certificates.',
  actions: [
    { label: 'Browse Exams', url: '/course', variant: 'primary' },
    { label: 'Register Now', url: '/sign-up', variant: 'secondary' }
  ],
  stats: [
    { value: '120+', label: 'certification exams' },
    { value: 'Live', label: 'attempt proctoring' },
    { value: '24/7', label: 'attempt window' }
  ],
  progressTitle: 'Attempt statistics',
  progressSubtitle: 'Your current exam progress',
  progressValue: '70%',
  showProgressCard: true,
  lessons: [
    { icon: '↻', title: '0 Attempts', label: 'Recorded' },
    { icon: '✓', title: '0 Exams', label: 'Completed' },
    { icon: '◷', title: '0 Exams', label: 'In progress' }
  ],
  floatingBadge: 'Live proctoring active ↗',
  growthTitle: 'Empowering certification through secure assessment',
  tracks: ['Aptitude & Reasoning', 'Programming', 'Cloud & DevOps', 'Data & AI', 'Domain Certification'],

  pathsKicker: 'How the exam portal works',
  pathsTitle: 'A clear path from registration to certificate.',
  pathsLead: 'Schedule an exam, attempt it under monitored conditions and receive automatic scoring with detailed section-wise analytics.',
  paths: [
    { title: 'Timed assessments', description: 'Configurable duration, sectional timing and auto-submit the moment an attempt expires.', tag: 'Exam control' },
    { title: 'Secure proctoring', description: 'Attempt monitoring with tab-switch detection and activity safeguards throughout the session.', tag: 'Integrity first' },
    { title: 'Instant results', description: 'Automatic scoring with section-wise analytics and a verified certificate on passing.', tag: 'Verified outcome' }
  ],

  coursesKicker: 'Featured exams',
  coursesTitle: 'Certifications that prove what you can do.',
  coursesLinkLabel: 'Browse all exams',
  coursesLinkUrl: '/course',
  courses: [
    { symbol: '</>', level: 'Proctored', duration: '90 min', title: 'Full-Stack Developer Certification', description: 'Assess front-end, API, database and deployment knowledge across a mixed question bank.', count: '60', countLabel: 'questions', note: 'Certificate →' },
    { symbol: '☁', level: 'Associate', duration: '75 min', title: 'Cloud & DevOps Associate Exam', description: 'Validate cloud fundamentals, CI/CD pipelines, containers and release reliability practices.', count: '50', countLabel: 'questions', note: 'Sectional timing →' },
    { symbol: 'AI', level: 'Advanced', duration: '120 min', title: 'Data & Applied AI Assessment', description: 'Test analytical reasoning, data modelling and applied AI judgement with scenario questions.', count: '80', countLabel: 'questions', note: 'Analytics →' }
  ],

  experienceKicker: 'The exam portal experience',
  experienceTitle: 'Everything an assessment needs, in one portal.',
  experienceLead: 'A controlled assessment environment combining scheduled attempts, monitored sessions, automatic scoring and verifiable certification.',
  features: [
    { title: 'Attempt from anywhere', description: 'Candidates sit scheduled exams on any device within the published attempt window.' },
    { title: 'Protected integrity', description: 'Tab-switch and activity safeguards keep every proctored attempt auditable.' },
    { title: 'Results you can verify', description: 'Automatic scoring, section-wise breakdowns and shareable certificates on passing.' }
  ],
  dashboardUser: 'GR',
  dashboardTitle: 'Your exam readiness',
  dashboardSubtitle: 'Track question-bank coverage before your scheduled attempt.',
  dashboardValue: '70%',
  dashboardRingProgress: '70%',
  dashboardRows: [
    { label: 'Question bank coverage', value: '82%' },
    { label: 'Mock attempts cleared', value: '61%' },
    { label: 'Passing readiness', value: '74%' }
  ],
  dashboardStats: [
    { value: '0%', label: 'exam completion' },
    { value: '0%', label: 'question coverage' }
  ],

  ctaTitle: 'Schedule your exam today. Get certified next.',
  ctaSubtitle: 'Pick a certification, book a proctored attempt window and receive verified results with detailed analytics.',
  ctaLabel: 'Browse Exams',
  ctaUrl: '/course',

  footerAbout: 'Secure, proctored online assessments with automatic scoring, section-wise analytics and verifiable certification.',
  footerColumns: [
    {
      heading: 'Exams',
      links: [
        { label: 'All exams', url: '/course' },
        { label: 'Exam categories', url: '/course#exam-catalog' },
        { label: 'About the portal', url: '/about' }
      ]
    }
  ],
  footerCopyright: '© 2026 Exam portal. All rights reserved.',
  socialLinks: []
};

/** Reads `key` from authored content, falling back to the design default. */
export const pick = (content, key) => {
  const value = content?.[key];
  if (value === undefined || value === null || value === '') return DEFAULTS[key];
  if (Array.isArray(value) && value.length === 0) return DEFAULTS[key];
  return value;
};

/** Reads a list without turning an intentional live empty state back into design defaults. */
export const pickList = (content, key) => (
  Array.isArray(content?.[key]) ? content[key] : pick(content, key)
);

export const visibleItems = (items) => (Array.isArray(items) ? items.filter((item) => item && item.visible !== false) : []);

/** Same-origin app route: '/course' yes, 'https://…', '#id', 'mailto:' no. */
const isInternalHref = (href) => {
  const value = String(href || '').trim();
  return value.startsWith('/') && !value.startsWith('//');
};

/** A left-click with no modifier keys — anything else keeps the browser's default behaviour. */
const isPlainClick = (event) => (
  event.button === 0
  && !event.metaKey
  && !event.ctrlKey
  && !event.shiftKey
  && !event.altKey
  && !event.defaultPrevented
);

/**
 * Link props for anchors inside the Exam template.
 *
 * The `href` is always present so links remain real links (middle-click, copy, crawlable, and
 * they still work with JS disabled). When the host app supplies `onNavigate`, an internal href
 * is routed through it instead, which keeps navigation client-side — a bare `<a href>` would
 * otherwise force a full document reload on every click. The Console preview passes no
 * `onNavigate`, and `previewPreventNavigation` there stops the anchors from doing anything.
 */
export const examLinkProps = (href, onNavigate) => {
  const target = href || '#';
  const isHashTarget = String(target).startsWith('#') && String(target).length > 1;

  if (onNavigate && isHashTarget) {
    return {
      href: target,
      onClick: (event) => {
        if (!isPlainClick(event)) return;
        const section = typeof document !== 'undefined'
          ? document.getElementById(String(target).slice(1))
          : null;
        if (!section) return;
        event.preventDefault();
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
  }

  if (!onNavigate || !isInternalHref(target)) return { href: target };

  return {
    href: target,
    onClick: (event) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      onNavigate(target);
    }
  };
};

/** Course descriptions are authored as rich text, so flatten to plain text before rendering. */
export const plainText = (value, limit = 160) => {
  const text = String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text;
};

const COURSE_SYMBOLS = ['</>', '☁', 'AI'];

const firstImageUrl = (value) => {
  if (!value) return '';
  const rawValue = typeof value === 'string'
    ? value
    : value.displayLink || value.previewUrl || value.url || value.storedLink || value.imageURL || value.imageUrl || '';
  if (!rawValue) return '';
  return /^https?:\/\//i.test(rawValue)
    ? rawValue
    : `https://managemybiz.s3.amazonaws.com/${rawValue}`;
};

export const getExamProductImageUrl = (product = {}) => {
  const sources = [product, product.product, product.scheduledProduct, product.scheduledProduct?.product].filter(Boolean);
  for (const source of sources) {
    const imageFromContents = (Array.isArray(source.imageContents) ? source.imageContents : [])
      .map(firstImageUrl)
      .find(Boolean);
    const imageFromList = (Array.isArray(source.imageUrlsList) ? source.imageUrlsList : [])
      .map(firstImageUrl)
      .find(Boolean);
    const imageUrl = imageFromContents
      || imageFromList
      || firstImageUrl(source.imageURL)
      || firstImageUrl(source.imageUrl)
      || firstImageUrl(source.imageContent)
      || firstImageUrl(source.image);
    if (imageUrl) return imageUrl;
  }
  return '';
};

export const getExamCategoryImageUrl = (category = {}) => firstImageUrl(
  [
    ...(Array.isArray(category.imageContents) ? category.imageContents : []),
    ...(Array.isArray(category.imageUrlsList) ? category.imageUrlsList : []),
    category.imageContent,
    category.imageURL,
    category.imageUrl,
    category.image
  ].map(firstImageUrl).find(Boolean)
);

/**
 * Maps a live course/product record onto an exam card. Accepts both the reader's product
 * shape (`name`) and the Console preview's sample shape (`title`) so the twins stay identical.
 */
export const courseToExamCard = (course = {}, index = 0, isLoggedIn = false) => ({
  symbol: course.symbol || COURSE_SYMBOLS[index % COURSE_SYMBOLS.length],
  level: course.category?.name || course.categoryName || course.level || 'Proctored',
  duration: course.duration || course.examDuration || '',
  title: course.name || course.title || 'Certification exam',
  description: plainText(course.description) || 'Validate your skills with a proctored assessment and receive verified results.',
  count: course.totalQuestions || course.questionCount || '',
  countLabel: 'questions',
  imageUrl: getExamProductImageUrl(course) || getExamCategoryImageUrl(course.category),
  note: 'View exam →',
  // href mirrors the legacy FeaturedCourses rule so the card still works without JS; when the
  // reader supplies onSelectCourse, the click instead goes through setSelectedProduct like the
  // Default course page does.
  url: course.primaryId
    ? (isLoggedIn ? '/course-detail' : '/sign-in')
    : (course.url || '/course'),
  source: course
});

/** Live categories become the track chips; falls back to authored content, then the design. */
const resolveTracks = (content, categories) => {
  const live = visibleItems(categories)
    .map((category) => ({
       label: category.name || category.title || category.categoryName,
      imageUrl: getExamCategoryImageUrl(category)
    }))
    .filter((category) => category.label);
  return live.length ? live : pick(content, 'tracks');
};

/** Live courses become the featured exam cards; falls back to authored content, then the design. */
const resolveCourses = (content, products, isLoggedIn) => {
  const live = visibleItems(products);
  if (Array.isArray(products)) return live.slice(0, 3).map((course, index) => courseToExamCard(course, index, isLoggedIn));
  return visibleItems(pick(content, 'courses'));
};

const numericValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

export const getExamQuestionCount = (course = {}) => {
  const countQuestions = (value, seen = new Set()) => {
    if (!value || typeof value !== 'object' || seen.has(value)) return 0;
    seen.add(value);
    if (Array.isArray(value)) return value.reduce((total, item) => total + countQuestions(item, seen), 0);

    const directCount = numericValue(value.totalQuestions ?? value.questionCount ?? value.noOfQuestions);
    if (directCount) return directCount;
    if (Array.isArray(value.questionList)) return value.questionList.length;

    return ['exams', 'examList', 'curriculums', 'curriculumList', 'curriculum']
      .reduce((total, key) => total + countQuestions(value[key], seen), 0);
  };

  return countQuestions(course);
};

export const getExamHomeMetrics = ({ products, categories } = {}) => {
  const liveProducts = Array.isArray(products) ? visibleItems(products) : [];
  const liveCategories = Array.isArray(categories) ? visibleItems(categories) : [];
  const questionCount = liveProducts.reduce((total, product) => total + getExamQuestionCount(product), 0);
  const examCount = liveProducts.length;
  const categoryCount = liveCategories.length;
  const maxMetric = Math.max(examCount, categoryCount, questionCount, 1);
  const metric = (label, value) => ({
    label,
    value: String(value),
    width: `${Math.round((value / maxMetric) * 100)}%`
  });

  return {
    examCount,
    categoryCount,
    questionCount,
    stats: [
      { value: String(examCount), label: 'available exams' },
      ...(questionCount ? [{ value: String(questionCount), label: 'questions in bank' }] : []),
      ...(categoryCount ? [{ value: String(categoryCount), label: 'exam categories' }] : [])
    ],
    dashboardRows: [
      metric('Available exams', examCount),
      ...(questionCount ? [metric('Questions in bank', questionCount)] : []),
      ...(categoryCount ? [metric('Exam categories', categoryCount)] : [])
    ],
    dashboardStats: [
      { value: String(examCount), label: 'available exams' },
      ...(questionCount ? [{ value: String(questionCount), label: 'questions configured' }] : []),
      ...(categoryCount ? [{ value: String(categoryCount), label: 'exam categories' }] : [])
    ]
  };
};

const asPercentage = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(100, Math.round(number))) : null;
};

const formatPercentage = (value, total) => {
  const numerator = Number(value);
  const denominator = Number(total);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) return null;
  return `${Math.round(Math.max(0, Math.min(1, numerator / denominator)) * 100)}%`;
};

const normalizedStatus = (value) => String(value || '').trim().replace(/[-\s]+/g, '_').toUpperCase();
const completedStatuses = new Set(['COMPLETED', 'EVALUATED', 'TERMINATED', 'PASSED', 'FAILED']);
const activeStatuses = new Set(['IN_PROGRESS', 'READY_TO_TAKE', 'NEW', 'STARTED']);

const getExamRecord = (record = {}) => record?.exam || record;
const getExamQuestions = (record = {}) => {
  const exam = getExamRecord(record);
  return Array.isArray(exam.questionList) ? exam.questionList : [];
};

const hasAnswer = (question = {}) => {
  const attempt = question.currentAttempt || question.attemptHistoryList?.[question.attemptHistoryList.length - 1];
  const answers = attempt?.selectedAnswersMap || attempt?.selectedAnswers || attempt?.answers;
  return Boolean(answers && (Array.isArray(answers) ? answers.length : Object.keys(answers).length));
};

const getScorePercentage = (record = {}) => {
  const grade = record.grade || record.exam?.grade || {};
  const explicit = asPercentage(grade.percentage ?? grade.scorePercentage ?? record.percentage);
  if (explicit !== null) return explicit;
  const obtained = Number(grade.obtainedMarks);
  const total = Number(grade.totalMarks);
  return total > 0 && Number.isFinite(obtained) ? asPercentage((obtained / total) * 100) : null;
};

/** Converts persisted user-exam records into the values shown in the live homepage cards. */
export const getExamAttemptStatistics = (records = []) => {
  const exams = (Array.isArray(records) ? records : [])
    .filter((record) => record && record.deleted !== true);
  const totalExams = exams.length;
  const completed = exams.filter((record) => completedStatuses.has(normalizedStatus(record.status || record.exam?.status))).length;
  const active = exams.filter((record) => activeStatuses.has(normalizedStatus(record.status || record.exam?.status))).length;
  const attempts = exams.reduce((total, record) => total + numericValue(record.totalNoOfTimesAttempted), 0);
  const totalQuestions = exams.reduce((total, record) => {
    const exam = getExamRecord(record);
    return total + numericValue(exam.noOfQuestions || getExamQuestions(record).length);
  }, 0);
  const answeredQuestions = exams.reduce((total, record) => total + getExamQuestions(record).filter(hasAnswer).length, 0);
  const questionCoverage = formatPercentage(answeredQuestions, totalQuestions);
  const completion = formatPercentage(completed, totalExams);
  const scores = exams.map(getScorePercentage).filter((value) => value !== null);
  const averageScore = scores.length ? `${Math.round(scores.reduce((sum, value) => sum + value, 0) / scores.length)}%` : null;
  const readinessValues = [questionCoverage, completion, averageScore]
    .filter(Boolean)
    .map((value) => Number.parseInt(value, 10));
  const readiness = readinessValues.length
    ? `${Math.round(readinessValues.reduce((sum, value) => sum + value, 0) / readinessValues.length)}%`
    : '0%';

  return {
    hasData: totalExams > 0,
    title: 'Attempt statistics',
    subtitle: totalExams ? 'Your current exam progress' : 'No exam attempts yet',
    progressValue: readiness,
    lessons: [
      { icon: '↻', title: `${attempts} Attempts`, label: 'Recorded' },
      { icon: '✓', title: `${completed} Exams`, label: 'Completed' },
      { icon: '◷', title: `${active} Exams`, label: 'In progress' }
    ],
    completion,
    questionCoverage,
    averageScore,
    totalExams,
    attempts,
    completed,
    active
  };
};

export const getExamReadinessContent = (attemptStatistics) => {
  const statistics = attemptStatistics || getExamAttemptStatistics([]);
  const rows = [
    ['Exam completion', statistics.completion],
    ['Question coverage', statistics.questionCoverage],
    ['Average score', statistics.averageScore]
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => ({ label, value, width: value }));

  return {
    value: statistics.progressValue || '0%',
    ringProgress: statistics.progressValue || '0%',
    rows,
    stats: rows.slice(0, 2).map((row) => ({ value: row.value, label: row.label.toLowerCase() }))
  };
};

const companyInitials = (companyName = '') => String(companyName)
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

export const resolveExamHomeLiveContent = (content, data = {}) => {
  // The builder supplies sample products without this marker. Only the reader's data bridge
  // can replace authored design values, which prevents an empty/loading API response from
  // reintroducing the old hardcoded homepage metrics.
  if (typeof data.examCatalogLoaded !== 'boolean') return content;

  const companyName = getExamCompanyName(data.company || data.companyDetails);
  if (!data.examCatalogLoaded) {
    return {
      ...content,
      brandName: companyName || (content.brandName === DEFAULTS.brandName ? 'Exam portal' : content.brandName),
      brandAccent: companyName ? '' : content.brandAccent,
      stats: [],
      showProgressCard: true,
      progressTitle: 'Attempt statistics',
      progressSubtitle: 'Loading live exam data…',
      progressValue: '0%',
      lessons: getExamAttemptStatistics([]).lessons,
      dashboardUser: companyInitials(companyName),
      dashboardTitle: companyName ? `${companyName} exam readiness` : 'Exam readiness',
      dashboardSubtitle: 'Loading live exam data…',
      dashboardValue: '—',
      dashboardRingProgress: '0%',
      dashboardRows: [],
      dashboardStats: [],
      experienceKicker: companyName ? `${companyName} exam experience` : 'Exam portal experience'
    };
  }

  const metrics = getExamHomeMetrics({ products: data.products, categories: data.categories });
  const attemptStatistics = data.attemptStatistics
    || data.examAttemptStatistics
    || data.attemptsSummary
    || getExamAttemptStatistics(data.userExams || data.examResults);
  const attemptLessons = visibleItems(attemptStatistics?.lessons || attemptStatistics?.sections);
  const attemptProgress = attemptStatistics?.progressValue || '0%';
  const readiness = getExamReadinessContent(attemptStatistics);
  return {
    ...content,
    brandName: companyName || (content.brandName === DEFAULTS.brandName ? 'Exam portal' : content.brandName),
    brandAccent: companyName ? '' : content.brandAccent,
    stats: metrics.stats,
    showProgressCard: true,
    progressTitle: attemptStatistics?.title || content.progressTitle,
    progressSubtitle: attemptStatistics?.subtitle || content.progressSubtitle,
    progressValue: attemptProgress,
    lessons: attemptLessons.length ? attemptLessons : getExamAttemptStatistics([]).lessons,
    dashboardUser: companyInitials(companyName),
    dashboardTitle: companyName ? `${companyName} exam readiness` : 'Exam catalog readiness',
    dashboardSubtitle: attemptStatistics?.hasData ? 'Readiness based on your exam activity' : 'Complete an exam to see your readiness',
    dashboardValue: readiness.value,
    dashboardRingProgress: readiness.ringProgress,
    dashboardRows: readiness.rows,
    dashboardStats: readiness.stats,
    experienceKicker: companyName ? `${companyName} exam experience` : 'Exam portal experience',
    footerCopyright: `© ${new Date().getFullYear()} ${companyName || 'Exam portal'}. All rights reserved.`
  };
};

/**
 * The template auth bridge carries profile details while the education data hook carries the
 * mounted Redux session flag. Login can update those sources on different renders, so either
 * positive signal must switch the exam chrome to its signed-in state.
 */
export const resolveExamAuth = (auth = {}, data = {}) => ({
  ...auth,
  isLoggedIn: Boolean(auth?.isLoggedIn || data?.isLoggedIn)
});

/**
 * Brand mark. Renders the company logo (company.logoImageContent.displayLink, via the shared
 * resolver) instead of a hard-coded name; falls back to the company name, then to the authored
 * brand text, so the header never looks empty when no logo is uploaded.
 */
export const ExamBrand = ({ content }) => {
  const companyState = useSelector(selectTemplateCompanyState);
  const company = getTemplateCompanyContext(companyState);
  const logoUrl = getCompanyLogoUrl(company);
  const companyName = company?.name || '';
  const [logoFailed, setLogoFailed] = React.useState(false);

  if (logoUrl && !logoFailed) {
    return (
      <span className="aae-brand">
        <img src={logoUrl} alt={`${companyName || 'Company'} logo`} onError={() => setLogoFailed(true)} />
      </span>
    );
  }

  if (companyName) return <span className="aae-brand">{companyName}</span>;

  return (
    <span className="aae-brand">
      {pick(content, 'brandName')}{content && Object.prototype.hasOwnProperty.call(content, 'brandAccent')
        ? (content.brandAccent ? <> <span>{content.brandAccent}</span></> : null)
        : <> <span>{pick(content, 'brandAccent')}</span></>}
    </span>
  );
};

/**
 * Footer blurb. Uses the live `company.description` (authored as rich text in the console, so it
 * is flattened to plain text) instead of a hard-coded blurb; falls back to the authored
 * `footerAbout` content when the company has no description.
 */
export const ExamFooterAbout = ({ content }) => {
  const companyState = useSelector(selectTemplateCompanyState);
  const company = getTemplateCompanyContext(companyState);
  const description = plainText(company?.description, 240);

  return <>{description || pick(content, 'footerAbout')}</>;
};

/**
 * Sign-in / sign-up nav entries are redundant once a candidate is signed in, so they are
 * dropped from the pill. Mirrors `isTemplateAuthRoute` in template/TemplateAuthState.jsx.
 */
const AUTH_ROUTES = ['/sign-in', '/signin', '/login', '/sign-up', '/signup', '/register'];
const AUTH_LABELS = ['sign in', 'signin', 'login', 'log in', 'sign up', 'signup', 'register'];

export const isExamAuthNavItem = (item = {}) => (
  AUTH_ROUTES.includes(String(item.url || item.route || '').trim().toLowerCase())
  || AUTH_LABELS.includes(String(item.label || item.title || '').trim().toLowerCase())
);

export const isExamCategoryNavItem = (item = {}) => (
  /\bcategor(?:y|ies)\b/i.test(String(item.label || item.title || '').trim())
  || String(item.url || item.route || '').toLowerCase().includes('#exam-catalog')
);

/**
 * Signed-in account control: avatar (photo when available, otherwise initials) linking to the
 * candidate's account page. Replaces the Login / Register pair.
 */
const ExamAccount = ({ auth, content, onNavigate }) => (
  <a
    className="aae-account"
    {...examLinkProps(auth.accountRoute || '/user-profile', onNavigate)}
    title={auth.displayName || 'Account'}
  >
    <span className="aae-account-avatar">
      {auth.avatarUrl
        ? <img src={auth.avatarUrl} alt="" />
        : <span aria-hidden="true">{auth.initials || 'A'}</span>}
    </span>
    <span className="aae-account-name">{auth.displayName || pick(content, 'accountLabel')}</span>
  </a>
);

export const ExamNav = ({ content, auth, onNavigate }) => {
  const isLoggedIn = Boolean(auth?.isLoggedIn);
  const navItems = visibleItems(pick(content, 'nav'))
    .filter((item) => !isExamCategoryNavItem(item))
    .filter((item) => !isLoggedIn || !isExamAuthNavItem(item));

  return (
    <header className="aae-nav">
      <a {...examLinkProps('/', onNavigate)} aria-label="Home">
        <ExamBrand content={content} />
      </a>
      <nav className="aae-nav-pill" aria-label="Primary navigation">
        {navItems.map((item, index) => (
          <a key={`${item.label}-${index}`} {...examLinkProps(item.url, onNavigate)}>{item.label}</a>
        ))}
      </nav>
      <div className="aae-nav-actions">
        {isLoggedIn ? (
          <ExamAccount auth={auth} content={content} onNavigate={onNavigate} />
        ) : (
          <>
            <a className="aae-login" {...examLinkProps(pick(content, 'signInUrl'), onNavigate)}>{pick(content, 'signInLabel')}</a>
            <a className="aae-register" {...examLinkProps(pick(content, 'registerUrl'), onNavigate)}>{pick(content, 'registerLabel')}</a>
          </>
        )}
      </div>
    </header>
  );
};

const SOCIAL_PLATFORMS = {
  linkedin: { label: 'LinkedIn', symbol: 'in', baseUrl: 'https://www.linkedin.com/company/' },
  facebook: { label: 'Facebook', symbol: 'f', baseUrl: 'https://www.facebook.com/' },
  youtube: { label: 'YouTube', symbol: '▶', baseUrl: 'https://www.youtube.com/@' }
};

export const normalizeExamSocialPlatform = (value = '') => {
  const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['linkedin', 'linkedln', 'iinkedin'].includes(normalized)) return 'linkedin';
  if (['facebook', 'fb'].includes(normalized)) return 'facebook';
  if (['youtube', 'yt'].includes(normalized)) return 'youtube';
  return '';
};

export const normalizeExamSocialUrl = (value = '', platform = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed === '#') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed) || /\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
  return SOCIAL_PLATFORMS[platform] ? `${SOCIAL_PLATFORMS[platform].baseUrl}${trimmed.replace(/^@/, '').replace(/^\/+/, '')}` : '';
};

export const getExamSocialLinks = (company = {}) => {
  const accounts = Array.isArray(company.socialMediaAccounts)
    ? company.socialMediaAccounts
    : Array.isArray(company.companyDetails?.socialMediaAccounts) ? company.companyDetails.socialMediaAccounts : [];
  return accounts.map((account) => {
    const platform = normalizeExamSocialPlatform(account?.name || account?.label || account?.platform || account?.type || account?.key);
    const url = normalizeExamSocialUrl(account?.value || account?.url || account?.route || account?.href || account?.link, platform);
    if (!platform || !url || account?.visible === false) return null;
    return { label: SOCIAL_PLATFORMS[platform].symbol, ariaLabel: SOCIAL_PLATFORMS[platform].label, url };
  }).filter(Boolean);
};

export const getExamCompanyName = (companySource = {}, fallback = '') => {
  const company = getTemplateCompanyContext(companySource);
  return company.name || company.companyName || company.businessName || fallback;
};

export const getExamChromeContent = (content = {}, companySource = {}) => {
  const companyName = getExamCompanyName(companySource);
  return companyName ? { ...content, brandName: companyName, brandAccent: '' } : content;
};

export const getExamFooterContent = (content = {}, companySource = {}) => {
  const company = getTemplateCompanyContext(companySource);
  const companyName = getExamCompanyName(company);
  const policies = getCompanyPolicyLinks(company);
  const policyLinks = policies.map((policy) => ({ label: policy.label, url: policy.route }));
  return {
    ...getExamChromeContent(content, company),
    footerColumns: [
      { heading: 'Exams', links: [
        { label: 'All exams', url: '/course' },
        { label: 'Exam categories', url: '/course#exam-catalog' },
        { label: 'About the portal', url: '/about' }
      ] },
      ...(policyLinks.length ? [{ heading: 'Legal', links: policyLinks }] : [])
    ],
    footerCopyright: `© ${new Date().getFullYear()} ${companyName || pick(content, 'brandName')}. All rights reserved.`,
    socialLinks: getExamSocialLinks(company)
  };
};

export const ExamFooter = ({ content, company, onNavigate }) => {
  const footerContent = getExamFooterContent(content, company);
  const footerColumns = visibleItems(footerContent.footerColumns);
  const socialLinks = visibleItems(footerContent.socialLinks);
  return (
  <footer className="aae-site-footer">
    <div className="aae-wrap">
      <div
        className="aae-footer-grid"
        style={{ gridTemplateColumns: `minmax(280px, 1.5fr) repeat(${footerColumns.length}, minmax(160px, 1fr))` }}
      >
        <div className="aae-footer-brand">
          <a {...examLinkProps('/', onNavigate)}><ExamBrand content={footerContent} /></a>
          <p><ExamFooterAbout content={footerContent} /></p>
        </div>
        {footerColumns.map((column, index) => (
          <div className="aae-footer-col" key={`${column.heading}-${index}`}>
            <h4>{column.heading}</h4>
            {visibleItems(column.links).map((link, linkIndex) => (
              <a key={`${link.label}-${linkIndex}`} {...examLinkProps(link.url, onNavigate)}>{link.label}</a>
            ))}
          </div>
        ))}
      </div>
      <div className="aae-footer-bottom">
        <span>{footerContent.footerCopyright}</span>
        {socialLinks.length ? <div className="aae-footer-social" aria-label="Social links">
          {socialLinks.map((link, index) => (
            <a
              key={`${link.label}-${index}`}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.ariaLabel || link.label}
            >
              {link.label}
            </a>
          ))}
        </div> : null}
      </div>
    </div>
  </footer>
  );
};

const ExamHero = ({ content, imageUrl, auth, onNavigate }) => (
  <section className="aae-hero">
    <div className="aae-wave w1" />
    <div className="aae-wave w2" />
    <div className="aae-wave w3" />
    <div className="aae-wave w4" />

    <div className="aae-hero-nav-slot">
      <ExamNav content={content} auth={auth} onNavigate={onNavigate} />
    </div>

    <div className="aae-hero-grid">
      <div>
        <div className="aae-eyebrow">{pick(content, 'eyebrow')}</div>
        <h1>
          {pick(content, 'titlePrefix')}
          <br />
          {pick(content, 'titleSuffix')}
          <span className="aae-accent">{pick(content, 'titleAccent')}</span>
        </h1>
        <p className="aae-hero-copy">{pick(content, 'subtitle')}</p>
        <div className="aae-cta-row">
          {visibleItems(pick(content, 'actions')).map((action, index) => (
            <a
              key={`${action.label}-${index}`}
              className={`aae-btn ${action.variant === 'secondary' || action.variant === 'outline' ? 'aae-btn-secondary' : 'aae-btn-primary'}`}
              {...examLinkProps(action.url, onNavigate)}
            >
              {action.label}
            </a>
          ))}
        </div>
        <div className="aae-micro-stats" aria-label="Academy highlights">
          {visibleItems(pickList(content, 'stats')).map((stat, index) => (
            <div className="aae-micro-stat" key={`${stat.label}-${index}`}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="aae-visual">
        <div className="aae-orbit" />
        {imageUrl ? (
          <img className="aae-student-photo" src={imageUrl} alt={pick(content, 'imageAlt') || 'Student learning on a laptop'} />
        ) : null}

        {content.showProgressCard !== false ? <aside className="aae-progress-card">
          <div className="aae-progress-head">
            <div>
              <h3>{pick(content, 'progressTitle')}</h3>
              <small>{pick(content, 'progressSubtitle')}</small>
            </div>
            <div className="aae-progress-num">{pick(content, 'progressValue')}</div>
          </div>
          <div className="aae-bar">
            <span style={{ width: pick(content, 'progressValue') }} />
          </div>
          <div className="aae-lesson-grid">
            {visibleItems(pickList(content, 'lessons')).map((lesson, index) => (
              <div className="aae-lesson" key={`${lesson.title}-${index}`}>
                <i>{lesson.icon}</i>
                <strong>{lesson.title}</strong>
                <span>{lesson.label}</span>
              </div>
            ))}
          </div>
        </aside> : null}

        <div className="aae-floating-badge">{pick(content, 'floatingBadge')}</div>
      </div>
    </div>
  </section>
);

const ExamGrowthBand = ({ content, tracks }) => (
  <section className="aae-growth">
    <div className="aae-wrap">
      <h2 className="aae-growth-title">{pick(content, 'growthTitle')}</h2>
      <div className="aae-track-row">
        {(tracks || []).slice(0, 5).map((track, index) => {
          const trackData = typeof track === 'string' ? { label: track } : track;
          return (
          <div className="aae-track" key={`${trackData.label}-${index}`}>
            {trackData.imageUrl ? <img src={trackData.imageUrl} alt="" aria-hidden="true" /> : null}
            <span>{trackData.label}</span>
          </div>
          );
        })}
      </div>
    </div>
  </section>
);

const ExamPaths = ({ content }) => (
  <section className="aae-paths">
    <div className="aae-wrap">
      <div className="aae-kicker">{pick(content, 'pathsKicker')}</div>
      <h2 className="aae-title">{pick(content, 'pathsTitle')}</h2>
      <p className="aae-lead">{pick(content, 'pathsLead')}</p>
      <div className="aae-cards">
        {visibleItems(pick(content, 'paths')).map((path, index) => (
          <article className="aae-card" key={`${path.title}-${index}`}>
            <h3>{path.title}</h3>
            <p>{path.description}</p>
            {path.tag ? <span className="aae-tag">{path.tag}</span> : null}
            <div className="aae-num">{String(index + 1).padStart(2, '0')}</div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ExamFeaturedCourses = ({ content, courses, onSelectCourse, onNavigate }) => (
  <section className="aae-courses">
    <div className="aae-wrap">
      <div className="aae-head">
        <div>
          <div className="aae-kicker">{pick(content, 'coursesKicker')}</div>
          <h2 className="aae-title">{pick(content, 'coursesTitle')}</h2>
        </div>
        <a className="aae-textlink" {...examLinkProps(pick(content, 'coursesLinkUrl'), onNavigate)}>
          {pick(content, 'coursesLinkLabel')} <span>↗</span>
        </a>
      </div>

      <div className="aae-course-grid">
        {courses.map((course, index) => (
          <a
            className="aae-course-card"
            href={course.url || '/course'}
            key={`${course.title}-${index}`}
            onClick={onSelectCourse && course.source
              ? (event) => { event.preventDefault(); onSelectCourse(course.source); }
              : undefined}
          >
            <div className={`aae-course-art a${(index % 3) + 1}${course.imageUrl ? ' has-product-image' : ''}`}>
              {course.imageUrl ? <img className="aae-product-image" src={course.imageUrl} alt="" onError={(event) => { event.currentTarget.remove(); }} /> : null}
              {!course.imageUrl ? <div className="aae-course-symbol">{course.symbol}</div> : null}
            </div>
            <div className="aae-course-body">
              <div className="aae-course-meta">
                {course.level ? <span className="aae-pill">{course.level}</span> : null}
                {course.duration ? <span className="aae-pill">{course.duration}</span> : null}
              </div>
              <h3>{course.title}</h3>
              <p>{course.description}</p>
              <div className="aae-course-foot">
                <span>{course.count ? <><strong>{course.count}</strong> {course.countLabel || 'questions'}</> : null}</span>
                <span>{course.note}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const ExamExperience = ({ content }) => (
  <section className="aae-experience">
    <div className="aae-wrap aae-experience-grid">
      <div>
        <div className="aae-kicker">{pick(content, 'experienceKicker')}</div>
        <h2 className="aae-title">{pick(content, 'experienceTitle')}</h2>
        <p className="aae-lead">{pick(content, 'experienceLead')}</p>

        <div className="aae-feature-list">
          {visibleItems(pick(content, 'features')).map((feature, index) => (
            <div className="aae-feature-item" key={`${feature.title}-${index}`}>
              <i>{String(index + 1).padStart(2, '0')}</i>
              <div>
                <strong>{feature.title}</strong>
                <span>{feature.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="aae-mock" aria-label="Learning dashboard preview">
        <div className="aae-mock-top">
          <div className="aae-mock-dots"><span /><span /><span /></div>
          <div className="aae-mock-user">{pick(content, 'dashboardUser')}</div>
        </div>
        <div className="aae-mock-panel">
          <h3>{pick(content, 'dashboardTitle')}</h3>
          <p>{pick(content, 'dashboardSubtitle')}</p>
          <div className="aae-mock-progress">
            <div className="aae-ring" style={{ '--aae-ring-progress': pick(content, 'dashboardRingProgress') }}><strong>{pick(content, 'dashboardValue')}</strong></div>
            <div className="aae-mock-bars">
              {visibleItems(pickList(content, 'dashboardRows')).map((row, index) => (
                <div className="aae-mock-row" key={`${row.label}-${index}`}>
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                  <div className="aae-tiny-bar"><i style={{ width: row.width || row.value }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="aae-mock-small-grid">
            {visibleItems(pickList(content, 'dashboardStats')).map((stat, index) => (
              <div className="aae-mock-small" key={`${stat.label}-${index}`}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  </section>
);

const ExamCta = ({ content, onNavigate }) => (
  <section className="aae-final-cta">
    <div className="aae-wrap">
      <div className="aae-cta-panel">
        <div>
          <h2>{pick(content, 'ctaTitle')}</h2>
          <p>{pick(content, 'ctaSubtitle')}</p>
        </div>
        <a className="aae-btn aae-btn-primary" {...examLinkProps(pick(content, 'ctaUrl'), onNavigate)}>
          {pick(content, 'ctaLabel')}
        </a>
      </div>
    </div>
  </section>
);

/**
 * Exam Portal home section.
 *
 * @param {object} props
 * @param {object} props.section the persisted section (authored content lives on `section.content`)
 * @param {object} [props.data] live education data — `{ products, categories, isLoggedIn }`.
 *   The reader passes the real academy courses/categories; the Console preview passes its
 *   sample set. When empty, authored content is used, then the design defaults.
 * @param {object} [props.auth] candidate auth state — `{ isLoggedIn, displayName, initials,
 *   avatarUrl, accountRoute }`. Supplied by the reader's auth bridge; the Console preview
 *   omits it, so the builder always shows the signed-out Login / Register pair.
 */
export default function EducationExamHomeSection({ section, data, auth, onSelectCourse, onNavigate }) {
  const authoredContent = section?.content || {};
  const resolvedAuth = resolveExamAuth(auth, data);
  const chromeContent = getExamChromeContent(authoredContent, data?.company || data?.companyDetails);
  const contentWithLiveCatalog = Array.isArray(data?.products)
    ? {
      ...chromeContent,
      stats: visibleItems(pick(chromeContent, 'stats')).map((stat, index) => (
        index === 0 ? { ...stat, value: String(visibleItems(data.products).length) } : stat
      ))
    }
    : chromeContent;
  const content = resolveExamHomeLiveContent(contentWithLiveCatalog, data);
  const imageUrl = content.studentImageUrl
    || content.imageUrl
    || content.heroImageUrl
    || DEFAULT_STUDENT_IMAGE;

  const tracks = resolveTracks(content, data?.categories);
  // Course links follow the same rule as the legacy FeaturedCourses component (signed in →
  // course detail, otherwise sign-in). Either auth source counts, so the card links agree with
  // what the nav is showing.
  const courses = resolveCourses(content, data?.products, resolvedAuth.isLoggedIn);

  return (
    <div className="aae-home" data-education-exam-home="true">
      <style>{EXAM_HOME_CSS}</style>
      <ExamHero content={content} imageUrl={imageUrl} auth={resolvedAuth} onNavigate={onNavigate} />
      <ExamGrowthBand content={content} tracks={tracks} />
      <ExamPaths content={content} />
      <ExamFeaturedCourses
        content={content}
        courses={courses}
        onSelectCourse={onSelectCourse}
        onNavigate={onNavigate}
      />
      <ExamExperience content={content} />
      <ExamCta content={content} onNavigate={onNavigate} />
      <ExamFooter content={content} company={data?.company || data?.companyDetails} onNavigate={onNavigate} />
    </div>
  );
}
